/**
 * @file Stimulus-контроллер формы фильтров каталога.
 * Управляет формой фильтров: заполняет поля из состояния, обновляет кнопку
 * «Применить» с количеством результатов, отображает выбранные фильтры внутри формы.
 * Передаёт значения в `CatalogState` при нажатии «Применить».
 */

import { Controller } from '@hotwired/stimulus'
import catalogState from '../stores/catalog_state'
import catalogModel from '../stores/catalog_model'
import { createFilterBtn } from '../utils/dom'
import { showPreloader, hidePreloader } from '../utils/preloader'

const SHOW_MORE_LIMIT = 8
// Минимальное время показа прелоудера: фильтрация синхронная и быстрая,
// без этого прелоудер мигал бы на один кадр.
const PRELOADER_MIN_VISIBLE_MS = 300
const SHOW_MORE_SELECTOR = '[data-catalog-filters-target~="show-more"]'
const SHOW_MORE_TOGGLE_SELECTOR =
  '[data-catalog-filters-target~="show-more-toggle"]'

/**
 * @class
 * @extends Controller
 */
export default class CatalogFiltersController extends Controller {
  static targets = [
    'form',
    'selectedFilters',
    'selectedFiltersList',
    'applyButton'
  ]

  static values = {
    resetFilterAriaLabel: { type: String, default: 'Reset filter' },
    loadingLabel: { type: String, default: '' },
    live: { type: Boolean, default: false }
  }

  #initialApplyButtonText = ''
  #showMoreExpanded = false

  /** Инициализация: восстановление выбранных фильтров из состояния, подписка на изменения формы. */
  connect() {
    if (this.hasApplyButtonTarget) {
      const btnTextEl = this.applyButtonTarget.querySelector('.btn__text')
      if (btnTextEl) {
        this.#initialApplyButtonText = btnTextEl.textContent
      }
    }

    const filters = catalogState.filters
    if (Object.keys(filters).length > 0) {
      this.formDataByName = filters
      this.#populateForm()
      this.#updateSelectedFilters()
      this.#updateApplyButton()
    }

    this.onFiltersChanged = this.onFiltersChanged.bind(this)
    this.formTarget.addEventListener('change', this.onFiltersChanged)
    this.#updateShowMore()
  }

  /** Отписка от события change формы. */
  disconnect() {
    this.formTarget.removeEventListener('change', this.onFiltersChanged)
  }

  /**
   * Применяет выбранные значения фильтров в `CatalogState` и закрывает модальное окно.
   * На время применения показывает полноэкранный прелоудер (как у модалки выбора языка).
   * @param {Event} event
   */
  apply(event) {
    event.preventDefault()
    this.formDataByName = this.#groupSelectedControlsByName()

    if (this.liveValue) {
      this.#syncLiveFilters()
      this.#closeModal()
      return
    }

    showPreloader({ label: this.loadingLabelValue })
    const shownAt = Date.now()

    // Двойной rAF: даём браузеру отрисовать прелоудер до синхронной фильтрации
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#applyFilters()

        const elapsed = Date.now() - shownAt
        setTimeout(
          () => hidePreloader(),
          Math.max(0, PRELOADER_MIN_VISIBLE_MS - elapsed)
        )
      })
    })
  }

  /** Передаёт выбранные фильтры в состояние и закрывает модальное окно. */
  #applyFilters() {
    catalogState.setFilters(this.formDataByName)
    this.#updateSelectedFilters()
    this.#closeModal()
  }

  /**
   * Удаляет отдельное значение фильтра внутри формы (без применения к состоянию).
   * @param {Event} event
   */
  removeFilter(event) {
    this.formDataByName = this.#groupSelectedControlsByName()
    const { filterName, filterValue } = event.currentTarget.dataset

    if (this.formDataByName[filterName]) {
      this.formDataByName[filterName] = this.formDataByName[filterName].filter(
        (v) => v.value !== filterValue
      )
    }

    this.#populateForm()

    if (this.formDataByName[filterName]?.length === 0) {
      delete this.formDataByName[filterName]
    }

    this.#updateSelectedFilters()
    this.#updateApplyButton()
    if (this.liveValue) catalogState.setFilters(this.formDataByName)
  }

  /** Сбрасывает форму, состояние фильтров и состояние «показать ещё». */
  reset() {
    this.formDataByName = {}
    this.formTarget.reset()
    this.#showMoreExpanded = false
    this.#updateSelectedFilters()
    this.#updateApplyButton()
    this.#updateShowMore()
    if (this.liveValue) catalogState.setFilters(this.formDataByName)
  }

  /** Обновляет форму и, в live-режиме, сразу применяет фильтры каталога. */
  onFiltersChanged() {
    if (this.liveValue) {
      this.formDataByName = this.#groupSelectedControlsByName()
      this.#syncLiveFilters()
    }

    this.#updateApplyButton()
  }

  /**
   * Разворачивает/сворачивает прокрутку длинных списков фильтров.
   * @param {Event} event
   */
  toggleShowMore(event) {
    event.preventDefault()
    this.#showMoreExpanded = !this.#showMoreExpanded
    this.#updateShowMore()
  }

  #populateForm() {
    Object.entries(this.formDataByName).forEach(([name, values]) => {
      const inputElements = this.formTarget.querySelectorAll(`[name="${name}"]`)
      const valuesArray = values.map((v) => v.value)
      inputElements.forEach((input) => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = valuesArray.includes(input.value)
        } else {
          input.value = valuesArray[0] || ''
        }
      })
    })
  }

  #updateSelectedFilters() {
    if (!this.hasSelectedFiltersTarget || !this.hasSelectedFiltersListTarget)
      return

    const selectedFiltersList = this.selectedFiltersListTarget
    const btnsToRemove = selectedFiltersList.querySelectorAll('.filter-btn')
    btnsToRemove.forEach((btn) => btn.remove())

    Object.entries(this.formDataByName).forEach(([name, values]) => {
      values.forEach((value) => {
        const filterBtn = createFilterBtn(value.textValue, true, true, {
          'data-action': 'click->catalog-filters#removeFilter',
          'data-filter-name': name,
          'data-filter-value': value.value,
          'aria-label': this.resetFilterAriaLabelValue
        })
        selectedFiltersList.appendChild(filterBtn)
      })
    })

    this.selectedFiltersTarget.classList.toggle(
      'hidden',
      // eslint-disable-next-line no-undefined
      this.formDataByName === undefined ||
        Object.keys(this.formDataByName).length === 0
    )
  }

  #selectedFormControls() {
    return Array.from(this.formTarget.elements).filter((element) => {
      if (!element.name) return false
      if (element.disabled) return false
      if (element.type === 'checkbox' || element.type === 'radio') {
        return element.checked
      }

      return element.value !== ''
    })
  }

  #groupSelectedControlsByName() {
    return this.#selectedFormControls().reduce((filters, input) => {
      filters[input.name] ||= []
      filters[input.name].push({
        value: input.value,
        textValue: input.dataset.textValue || input.value
      })

      return filters
    }, {})
  }

  #closeModal() {
    const modalElement = this.formTarget.closest('[data-controller~="modal"]')
    const modalController =
      modalElement &&
      this.application.getControllerForElementAndIdentifier(
        modalElement,
        'modal'
      )

    modalController?.close()
  }

  #syncLiveFilters() {
    catalogState.setFilters(this.formDataByName)
    this.#updateSelectedFilters()
  }

  #updateApplyButton() {
    if (!this.hasApplyButtonTarget) return
    const count = catalogModel.count(this.#groupSelectedControlsByName())
    const btnTextEl = this.applyButtonTarget.querySelector('.btn__text')
    if (btnTextEl) {
      btnTextEl.textContent = `${this.#initialApplyButtonText} (${count})`
    }
  }

  #updateShowMore() {
    this.element.querySelectorAll(SHOW_MORE_SELECTOR).forEach((container) => {
      this.#updateShowMoreContainer(container)
    })
  }

  #updateShowMoreContainer(container) {
    const items = Array.from(
      container.querySelectorAll('[data-show-more-item]')
    )
    const limit =
      Number.parseInt(container.dataset.catalogFiltersShowMoreLimitValue, 10) ||
      SHOW_MORE_LIMIT
    const hasHiddenItems = items.length > limit
    const shouldHideExtraItems = hasHiddenItems && !this.#showMoreExpanded

    items.forEach((item, index) => {
      item.hidden = shouldHideExtraItems && index >= limit
    })

    container.querySelectorAll('[data-show-more-group]').forEach((group) => {
      const groupItems = Array.from(
        group.querySelectorAll('[data-show-more-item]')
      )
      group.hidden =
        groupItems.length > 0 && groupItems.every((item) => item.hidden)
    })

    this.#updateShowMoreToggle(container, hasHiddenItems)
  }

  #updateShowMoreToggle(container, hasHiddenItems) {
    const toggle = this.#showMoreToggleFor(container)

    if (!toggle) return

    toggle.hidden = !hasHiddenItems
    toggle.setAttribute('aria-expanded', String(this.#showMoreExpanded))

    const linkTextElement = toggle.querySelector('.link__text')
    if (linkTextElement) {
      const showMoreText =
        container.dataset.catalogFiltersShowMoreValue || 'Show more'
      const showLessText =
        container.dataset.catalogFiltersShowLessValue || 'Show less'

      linkTextElement.textContent = this.#showMoreExpanded
        ? showLessText
        : showMoreText
    }

    const icon = toggle.querySelector('svg')
    if (icon) {
      icon.classList.toggle('rotate-180', this.#showMoreExpanded)
    }
  }

  #showMoreToggleFor(container) {
    return container
      .closest('fieldset')
      ?.querySelector(SHOW_MORE_TOGGLE_SELECTOR)
  }
}
